'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { SectionOf } from '@/lib/home-v2/schema';
import { WRAP } from './ui';

type Data = SectionOf<'offer-banner'>;
type Item = Data['items'][number];

const SPACING = {
  none: '',
  small: 'pt-4 md:pt-6',
  normal: 'pt-8 md:pt-12',
  large: 'pt-14 md:pt-20',
};

const RATIO = {
  small: 'aspect-[3/1] md:aspect-[6/1]',
  medium: 'aspect-[2/1] md:aspect-[4/1]',
  card: 'aspect-[3/2]',
};

function Slide({ item, ratio, style, priority, compact, square }: { item: Item; ratio: string; style?: CSSProperties; priority: boolean; compact: boolean; square?: boolean }) {
  const caption = item.title || item.text || item.ctaLabel;
  const body = (
    // Grid stack: a ratio-only spacer and the caption share one cell, so the banner is as tall as
    // the ratio asks for OR as tall as the caption needs, whichever is greater. An aspect-ratio on
    // the box itself caps the height instead, which clipped captions on narrow phones.
    <div className={`relative grid w-full overflow-hidden bg-hv-card ${square ? '' : 'rounded-2xl'}`}>
      <Image src={item.image} alt={item.alt} fill sizes={compact ? '(min-width: 768px) 440px, 86vw' : '(min-width: 768px) 1312px, 100vw'}
        priority={priority} className={`object-cover ${item.mobileImage ? 'hidden md:block' : ''}`} />
      {item.mobileImage && (
        <Image src={item.mobileImage} alt={item.alt} fill sizes="100vw" priority={priority} className="object-cover md:hidden" />
      )}
      <div className={`col-start-1 row-start-1 w-full ${ratio}`} style={style} aria-hidden="true" />
      {caption && (
        <div className={`relative z-[1] col-start-1 row-start-1 flex w-full bg-gradient-to-r from-black/65 via-black/25 to-transparent ${compact ? 'items-end' : 'items-center'}`}>
          <div className={`flex flex-col gap-0.5 text-white sm:gap-1 ${compact ? 'max-w-[85%] p-3 sm:p-4 md:p-5' : 'max-w-[72%] px-4 py-2 sm:max-w-[70%] sm:px-5 sm:py-3 md:max-w-[55%] md:gap-2 md:px-10'}`}>
            {/* Fluid type: the caption scales with the viewport so it fits the banner's shape
                instead of forcing the box taller on narrow phones. */}
            {item.title && (
              <div className="font-semibold leading-tight line-clamp-2"
                style={{ fontSize: compact ? 'clamp(14px, 3.8vw, 20px)' : 'clamp(14px, 4vw, 30px)' }}>
                {item.title}
              </div>
            )}
            {item.text && (
              <div className="line-clamp-2 text-white/85"
                style={{ fontSize: compact ? 'clamp(10px, 2.9vw, 14px)' : 'clamp(10px, 3vw, 16px)' }}>
                {item.text}
              </div>
            )}
            {item.ctaLabel && (
              <span className={`mt-1 inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-lg bg-hv-yellow px-2.5 py-1 font-semibold text-hv-ink ${compact ? '' : 'md:mt-2 md:rounded-xl md:px-5 md:py-2.5'}`}
                style={{ fontSize: 'clamp(10px, 2.8vw, 14px)' }}>
                {item.ctaLabel} →
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
  return item.href ? <Link href={item.href} className="block" aria-label={item.alt} draggable={false}>{body}</Link> : body;
}

function Arrow({ dir, onClick }: { dir: 1 | -1; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label={dir === 1 ? 'Next offer' : 'Previous offer'}
      className={`absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-hv-ink shadow-md transition hover:bg-white md:flex md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 ${dir === 1 ? 'right-3' : 'left-3'}`}>
      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={dir === 1 ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} />
      </svg>
    </button>
  );
}

/** Swipeable, auto-playing track used by the "carousel" (one at a time) and "slider" (several in view) layouts. */
function Track({ data, items, ratio, ratioStyle, square }: { data: Data; items: Item[]; ratio: string; ratioStyle?: CSSProperties; square: boolean }) {
  const slider = data.layout === 'slider';
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [positions, setPositions] = useState(items.length);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Distance between two slide starts (slide width + gap)
  const step = useCallback(() => {
    const el = trackRef.current;
    const [a, b] = el ? Array.from(el.children) as HTMLElement[] : [];
    if (!a) return 1;
    return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
  }, []);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const s = step();
    setPositions(Math.max(1, Math.round((el.scrollWidth - el.clientWidth) / s) + 1));
    setActive(Math.round(el.scrollLeft / s));
  }, [step]);

  const goTo = useCallback((i: number) => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: i * step(), behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [step, reducedMotion]);

  const move = useCallback((dir: 1 | -1) => {
    goTo((active + dir + positions) % positions);
  }, [active, positions, goTo]);

  useEffect(() => {
    setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const el = trackRef.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      io.disconnect();
    };
  }, [measure]);

  // Auto-play only while on screen, not hovered/focused/touched, and when motion is allowed
  useEffect(() => {
    if (!data.autoPlay || paused || !visible || reducedMotion || positions < 2) return;
    const t = setInterval(() => {
      if (!document.hidden) move(1);
    }, data.autoPlayMs);
    return () => clearInterval(t);
  }, [data.autoPlay, data.autoPlayMs, paused, visible, reducedMotion, positions, move]);

  const slideWidth = slider
    ? 'w-[86%] md:w-[var(--hv-slide)]'
    : 'w-full';
  const showArrows = data.arrows && positions > 1;
  const showDots = data.dots && positions > 1;

  return (
    <div className="group relative" aria-roledescription="carousel" aria-label={data.title ?? 'Offers'}
      style={{ '--hv-slide': `calc((100% - ${(data.perView - 1) * 16}px) / ${data.perView})` } as CSSProperties}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') move(1);
        if (e.key === 'ArrowLeft') move(-1);
      }}>
      <div ref={trackRef} className={`scrollbar-hide flex snap-x snap-mandatory gap-3 overflow-x-auto md:gap-4 ${slider ? '-mx-4 scroll-px-4 px-4 md:mx-0 md:scroll-px-0 md:px-0' : ''}`}>
        {items.map((item, k) => (
          <div key={`${item.image}-${k}`} role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${items.length}`}
            className={`${slideWidth} flex-shrink-0 snap-start`}>
            <Slide item={item} ratio={ratio} style={ratioStyle} priority={k === 0} compact={slider} square={square} />
          </div>
        ))}
      </div>

      {showArrows && (
        <>
          <Arrow dir={-1} onClick={() => move(-1)} />
          <Arrow dir={1} onClick={() => move(1)} />
        </>
      )}

      {showDots && (
        <div className={slider
          ? 'mt-3 flex justify-center gap-1.5'
          : 'absolute bottom-2.5 right-3 flex items-center gap-1.5 md:bottom-4 md:right-5'}>
          {Array.from({ length: positions }, (_, k) => (
            <button key={k} type="button" aria-label={`Show offer ${k + 1}`} aria-current={k === active} onClick={() => goTo(k)}
              className={`h-1.5 rounded-full transition-all ${k === active
                ? `w-6 ${slider ? 'bg-hv-ink' : 'bg-white'}`
                : `w-1.5 ${slider ? 'bg-hv-ink/25' : 'bg-white/50'}`}`} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Image banner(s) from the config: carousel, multi-card slider, a static row, or a single banner. */
export function OfferBanner({ data, items }: { data: Data; items: Item[] }) {
  const count = items.length;
  const full = data.width === 'full';
  // A custom ratio wins over `size`; phones keep the taller crop so wide strips stay readable
  const ratio = data.ratio ? 'aspect-[2/1] md:aspect-[var(--hv-ratio)]' : RATIO[data.size];
  const ratioStyle = data.ratio ? ({ '--hv-ratio': data.ratio } as CSSProperties) : undefined;

  let content;
  if ((data.layout === 'carousel' || data.layout === 'slider') && count > 1) {
    content = <Track data={data} items={items} ratio={ratio} ratioStyle={ratioStyle} square={full} />;
  } else if (data.layout === 'row' && count > 1) {
    content = (
      <div className="scrollbar-hide -mx-4 flex snap-x gap-3 overflow-x-auto px-4 md:mx-0 md:grid md:gap-4 md:overflow-visible md:px-0"
        style={{ gridTemplateColumns: `repeat(${Math.min(count, 3)}, minmax(0, 1fr))` }}>
        {items.slice(0, 3).map((item, k) => (
          <div key={item.image} className="w-[85%] flex-shrink-0 snap-start md:w-auto">
            <Slide item={item} ratio={ratio} style={ratioStyle} priority={k === 0} compact={false} />
          </div>
        ))}
      </div>
    );
  } else {
    content = <Slide item={items[0]} ratio={ratio} style={ratioStyle} priority compact={false} square={full} />;
  }

  return (
    <section className={`${full ? 'w-full' : WRAP} ${SPACING[data.spacing]}`} aria-label={data.title ?? 'Offers'}>
      {data.title && <h2 className={`mb-4 text-2xl leading-tight tracking-[-0.02em] md:mb-5 md:text-[32px] ${full ? WRAP : ''}`}>{data.title}</h2>}
      {content}
    </section>
  );
}
